/**
 * Recording Service for Child App
 * Handles fetching, uploading, and managing audio recordings via the backend API.
 * All calls use device authentication (deviceId + familyId).
 *
 * Requirements: 7.4, 8.1, 14.1-14.5
 */

import { api } from './api.js';
import { getDeviceId } from './deviceAuth.js';

/**
 * Get the current family ID from localStorage.
 * Exported so the recording store can scope pending uploads to the same family.
 * @returns {string|null}
 */
export function getFamilyId() {
  try {
    const stored = localStorage.getItem('authorizedFamily');
    if (stored) {
      const family = JSON.parse(stored);
      return family.id || null;
    }
  } catch (e) {
    console.error('❌ Failed to get family ID:', e);
  }
  return null;
}

/**
 * Fetch all recordings for a story (filtered by family).
 * Returns recordings grouped by type and profile.
 *
 * Adult (family_narration) recordings are family-wide: every profile in the
 * family may listen to them. Child recordings are private to the child who
 * made them, so pass `childProfileId` to keep other children's recordings out
 * of the result.
 *
 * @param {string} moduleId - Module/story ID
 * @param {Object} [options] - Optional filters
 * @param {string} [options.recordingType] - "child" or "family_narration"
 * @param {string} [options.profileId] - Server-side filter by specific profile
 * @param {string} [options.childProfileId] - Only keep child recordings owned by this profile
 * @returns {Promise<{recordings: Array, adultRecordings: Array, childRecordings: Map}>}
 */
export async function fetchStoryRecordings(moduleId, options = {}) {
  const deviceId = getDeviceId();
  const familyId = getFamilyId();

  if (!familyId) {
    console.warn('⚠️ No family ID available, cannot fetch recordings');
    return { recordings: [], adultRecordings: [], childRecordings: new Map() };
  }

  try {
    const params = {
      deviceId,
      familyId,
    };
    if (options.recordingType) params.recordingType = options.recordingType;
    if (options.profileId) params.profileId = options.profileId;

    const response = await api.get(`/api/recordings/story/${moduleId}`, params);

    const recordings = response.recordings || [];

    // Group adult recordings by profile (for Audio Mode Menu)
    const adultMap = new Map();
    const childRecordings = new Map(); // pageNumber → recording

    recordings.forEach((rec) => {
      if (rec.recordingType === 'family_narration') {
        const key = rec.profileId;
        if (!adultMap.has(key)) {
          adultMap.set(key, {
            profileId: rec.profileId,
            name: rec.displayName || 'Family',
            avatarUrl: rec.avatarUrl || '/svg/components/GrownUp-1.svg',
            pages: new Map(),
          });
        }
        adultMap.get(key).pages.set(rec.pageNumber, rec.audioUrl + `?t=${Date.now()}`);
      } else if (rec.recordingType === 'child') {
        // Child recordings are private: skip recordings belonging to siblings.
        if (options.childProfileId && rec.profileId !== options.childProfileId) {
          return;
        }
        childRecordings.set(rec.pageNumber, {
          url: rec.audioUrl + `?t=${Date.now()}`,
          status: 'ready',
          storyPage: rec.pageNumber,
          recordingId: rec.id,
          profileId: rec.profileId,
        });
      }
    });

    // Convert adult map to array for the Audio Mode Menu
    const adultRecordings = Array.from(adultMap.values());

    console.log(
      `🎵 Fetched recordings for story ${moduleId}:`,
      `${childRecordings.size} child,`,
      `${adultRecordings.length} adult narrators`
    );

    return { recordings, adultRecordings, childRecordings };
  } catch (error) {
    console.error('❌ Failed to fetch story recordings:', error);
    return { recordings: [], adultRecordings: [], childRecordings: new Map() };
  }
}

/**
 * Upload a recording for a specific page.
 *
 * @param {Blob} audioBlob - Audio blob from MediaRecorder
 * @param {Object} metadata
 * @param {string} metadata.profileId - Child or adult profile ID
 * @param {string} metadata.moduleId - Story/module ID
 * @param {number} metadata.pageNumber - Page number (1-based)
 * @param {string} [metadata.recordingType] - "child" or "family_narration"
 * @param {string} [metadata.displayName] - Display name for family narration
 * @returns {Promise<Object>} Upload result
 */
export async function uploadRecording(audioBlob, metadata) {
  const deviceId = getDeviceId();
  const familyId = metadata.familyId || getFamilyId();

  if (!familyId) {
    throw new Error('No family ID available for upload');
  }

  const formData = new FormData();
  formData.append('file', audioBlob, `page_${metadata.pageNumber}.webm`);
  formData.append('deviceId', deviceId);
  formData.append('profileId', metadata.profileId);
  formData.append('familyId', familyId);
  formData.append('moduleId', metadata.moduleId);
  formData.append('pageNumber', String(metadata.pageNumber));
  formData.append('recordingType', metadata.recordingType || 'child');
  if (metadata.displayName) {
    formData.append('displayName', metadata.displayName);
  }
  if (metadata.durationSeconds !== null && metadata.durationSeconds !== undefined) {
    formData.append('durationSeconds', String(metadata.durationSeconds));
  }

  // Use fetch directly for multipart form upload (api.post sends JSON)
  const API_BASE_URL = getApiBaseUrl();
  const response = await fetch(`${API_BASE_URL}/api/recordings/upload`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Upload failed (${response.status}): ${errorText}`);
  }

  const result = await response.json();
  console.log(`✅ Recording uploaded: page ${metadata.pageNumber}`, result);
  return result;
}

/**
 * Delete a recording by ID.
 *
 * @param {string} recordingId - Recording document ID
 * @returns {Promise<Object>} Delete result
 */
export async function deleteRecording(recordingId) {
  const deviceId = getDeviceId();
  const familyId = getFamilyId();

  if (!familyId) {
    throw new Error('No family ID available for delete');
  }

  const result = await api.delete(
    `/api/recordings/${recordingId}?deviceId=${deviceId}&familyId=${familyId}`
  );
  console.log(`🗑️ Recording deleted: ${recordingId}`);
  return result;
}

/**
 * Get the API base URL (mirrors logic from api.js).
 * @returns {string}
 */
function getApiBaseUrl() {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8000';
  }
  if (hostname.startsWith('192.168.') || hostname.startsWith('10.')) {
    return 'http://localhost:8000';
  }
  return 'https://api.kassie.media';
}
